/**
 * `ExternalCvList` — pagina elenco/creazione dei profili CV esterni.
 *
 * Rotta `/external-cv` (sezione "CV esterni", accesso `requireLevelsAccess`
 * lato API; guardia difensiva `LevelsRoute` lato client). Consente di:
 *  - elencare i profili CV esterni (`GET /api/external-cv/profiles`) con nome,
 *    cognome, data di creazione ed esito della pre-compilazione AI (Req. 3.1);
 *  - creare un nuovo profilo caricando un CV in PDF. L'upload segue lo schema
 *    presigned già usato per le immagini (nessun transito del file attraverso
 *    l'API):
 *      1. `POST /api/external-cv/uploads/presign { contentType: 'application/pdf' }`
 *         → `uploadUrl` / `key` firmati (Req. 1.4);
 *      2. `PUT` diretto del PDF su S3 verso `uploadUrl` con
 *         `Content-Type: application/pdf`;
 *      3. `POST /api/external-cv/profiles { firstName, lastName, cvPdfKey }`
 *         → crea il profilo e avvia la pre-compilazione AI (Req. 1.1, 1.5, 2).
 *
 * Ogni riga rimanda al dettaglio del profilo (`/external-cv/:id`). Lo stato
 * `aiStatus` è mostrato con un badge; quando la pre-compilazione è fallita si
 * segnala esplicitamente l'esito (Req. 2.5). L'autorizzazione autorevole resta
 * l'API: dopo ogni mutazione si invalidano le query pertinenti.
 */
import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CandidateSignal,
  ExternalCvAiStatus,
  ExternalCvProfileDetail,
  ExternalCvProfileSummary,
  PresignCertificationImageResponse,
} from '../shared/dto';
import { useApiClient } from '../api/useApiClient';
import { scoreColor } from '../components/StarRating';
import {
  Alert,
  Badge,
  Button,
  DataTable,
  Field,
  Icon,
  type IconName,
  Loading,
  Modal,
  PageHeader,
  TextInput,
  type BadgeTone,
  type Column,
  type SortDirection,
} from '../components/ui';

const externalCvProfilesKey = ['external-cv', 'profiles'] as const;

/** Content-type dell'unico formato ammesso per il Cv_Pdf. */
const PDF_CONTENT_TYPE = 'application/pdf';

/** Corpo della creazione profilo (`POST /api/external-cv/profiles`). */
interface CreateExternalCvProfileBody {
  firstName: string;
  lastName: string;
  cvPdfKey: string;
}

/** Tono del badge per ciascun esito della pre-compilazione AI. */
const AI_STATUS_TONE: Record<ExternalCvAiStatus, BadgeTone> = {
  pending: 'info',
  completed: 'success',
  failed: 'danger',
};

/** Etichetta (UI copy) per ciascun esito della pre-compilazione AI. */
const AI_STATUS_LABEL: Record<ExternalCvAiStatus, string> = {
  pending: 'In elaborazione',
  completed: 'Completata',
  failed: 'Non riuscita',
};

/**
 * Icona (invece del testo) per ciascun esito della pre-compilazione AI,
 * mostrata dentro il chip che conserva i colori del tono:
 *  - `pending`   → orologio (in elaborazione);
 *  - `completed` → spunta (completata);
 *  - `failed`    → croce (non riuscita).
 */
const AI_STATUS_ICON: Record<ExternalCvAiStatus, IconName> = {
  pending: 'clock',
  completed: 'check',
  failed: 'close',
};

/**
 * Icona della segnalazione candidato, mostrata davanti al nome:
 *  - `promising`  → doppio chevron su (interesse alto);
 *  - `toEvaluate` → singolo chevron su (interesse medio).
 */
const SIGNAL_ICON: Record<CandidateSignal, IconName> = {
  promising: 'chevrons-up',
  toEvaluate: 'chevron-up',
};

/** Etichetta accessibile della segnalazione candidato (tooltip + screen reader). */
const SIGNAL_LABEL: Record<CandidateSignal, string> = {
  promising: 'Promettente',
  toEvaluate: 'Da valutare',
};

/** Formatta una data ISO-8601 nel formato locale italiano (data breve). */
function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('it-IT', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Placeholder per un valore non disponibile (es. `topProficiencyPercent = null`). */
const PLACEHOLDER = '—';

/**
 * Formatta una percentuale `0..100`; `null` → placeholder. I valori oltre il
 * 100% (bonus certificazioni) sono mostrati come "100%+", coerentemente col
 * trattamento oro del resto del software (allineato alla vista Team).
 */
function formatMatch(value: number | null): string {
  if (value === null) return PLACEHOLDER;
  return value > 100 ? '100%+' : `${Math.round(value)}%`;
}

/**
 * Barra di percentuale (`0..100`) colorata con la scala rosso→verde dei voti
 * (`scoreColor`, percentuale riportata su `0..10`). Affianca il valore numerico.
 * Identica alla `PercentBar` della vista Team ("Livelli"). `label` personalizza
 * il testo accessibile.
 */
function PercentBar({ value, label }: { value: number; label: string }) {
  // Il valore può superare 100 (bonus certificazioni): la barra si riempie al
  // massimo e assume il trattamento oro con riflesso; l'etichetta mostra "100%+".
  const isOverMax = value > 100;
  const width = Math.min(100, Math.max(0, Math.round(value)));
  const exact = Math.round(Math.max(0, value));
  const color = scoreColor(value / 10) ?? undefined;
  return (
    <span
      className="coverage-bar"
      role="progressbar"
      aria-valuenow={exact}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={isOverMax ? `${label} oltre 100% (${exact}%)` : `${label} ${width}%`}
    >
      <span
        className={[
          'coverage-bar__fill',
          isOverMax ? 'coverage-bar__fill--gold' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={{
          width: `${width}%`,
          ...(!isOverMax && color ? { background: color } : {}),
        }}
      />
    </span>
  );
}

/** Confronto per cognome+nome, orientato dalla direzione (per `DataTable`). */
function compareByName(
  a: ExternalCvProfileSummary,
  b: ExternalCvProfileSummary,
  direction: SortDirection,
): number {
  const sign = direction === 'desc' ? -1 : 1;
  const va = `${a.lastName} ${a.firstName}`.toLowerCase();
  const vb = `${b.lastName} ${b.firstName}`.toLowerCase();
  if (va < vb) return -sign;
  if (va > vb) return sign;
  return 0;
}

export default function ExternalCvList() {
  const api = useApiClient();
  const queryClient = useQueryClient();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  // Ricerca client-side per nome/cognome o professione principale
  // (case-insensitive, sottostringa).
  const [query, setQuery] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const profilesQuery = useQuery({
    queryKey: externalCvProfilesKey,
    queryFn: () => api.get<ExternalCvProfileSummary[]>('/api/external-cv/profiles'),
  });

  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setPdfFile(null);
    setFormError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const createProfile = useMutation({
    /**
     * Orchestrazione dell'upload presigned + creazione del profilo:
     *  1. presign PDF; 2. PUT diretto su S3; 3. creazione profilo.
     */
    mutationFn: async (vars: {
      firstName: string;
      lastName: string;
      file: File;
    }): Promise<ExternalCvProfileDetail> => {
      // 1) URL di upload firmato (solo application/pdf).
      const presigned = await api.post<PresignCertificationImageResponse>(
        '/api/external-cv/uploads/presign',
        { contentType: PDF_CONTENT_TYPE },
      );

      // 2) PUT diretto su S3. NB: si usa `fetch` nativo (non il client API), che
      // non deve allegare l'Authorization Bearer né alterare gli header: S3
      // valida la firma sul solo `Content-Type`, che deve combaciare.
      const putResponse = await fetch(presigned.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': presigned.contentType },
        body: vars.file,
      });
      if (!putResponse.ok) {
        throw new Error(`Upload non riuscito (HTTP ${putResponse.status})`);
      }

      // 3) Crea il profilo con la chiave del PDF caricato (avvia l'AI).
      const body: CreateExternalCvProfileBody = {
        firstName: vars.firstName,
        lastName: vars.lastName,
        cvPdfKey: presigned.key,
      };
      return api.post<ExternalCvProfileDetail>('/api/external-cv/profiles', body);
    },
    onSuccess: () => {
      resetForm();
      setIsCreateOpen(false);
      void queryClient.invalidateQueries({ queryKey: externalCvProfilesKey });
    },
    onError: () => {
      setFormError('Creazione del profilo non riuscita. Riprova.');
    },
  });

  const profiles = useMemo(() => profilesQuery.data ?? [], [profilesQuery.data]);

  // Filtro client-side: confronta la query (trim + lowercase) con "cognome
  // nome" e con la professione principale. Query vuota → lista invariata.
  const normalizedQuery = query.trim().toLowerCase();
  const visibleProfiles = useMemo(() => {
    if (normalizedQuery === '') return profiles;
    return profiles.filter((p) => {
      const fullName = `${p.lastName} ${p.firstName}`.toLowerCase();
      const job = (p.topJobName ?? '').toLowerCase();
      return (
        fullName.includes(normalizedQuery) || job.includes(normalizedQuery)
      );
    });
  }, [profiles, normalizedQuery]);

  const trimmedFirstName = firstName.trim();
  const trimmedLastName = lastName.trim();
  const canSubmit =
    trimmedFirstName.length > 0 && trimmedLastName.length > 0 && pdfFile !== null;

  const columns = useMemo<Column<ExternalCvProfileSummary>[]>(
    () => [
      {
        key: 'name',
        header: 'Candidato',
        sortable: true,
        sortComparator: compareByName,
        render: (p) => (
          <div className="coverage-name external-cv-list__candidate">
            {p.signal !== null && (
              <Icon
                name={SIGNAL_ICON[p.signal]}
                size={16}
                title={SIGNAL_LABEL[p.signal]}
                className={`external-cv-list__signal external-cv-list__signal--${p.signal}`}
              />
            )}
            <Link
              to={`/external-cv/${p.id}`}
              className="coverage-name__link"
              aria-label={`Apri il profilo di ${p.lastName} ${p.firstName}`}
            >
              {p.lastName} {p.firstName}
              <Icon name="external" size={14} className="coverage-name__link-icon" />
            </Link>
          </div>
        ),
      },
      {
        key: 'topCompetence',
        header: 'Competenza',
        sortable: true,
        sortAccessor: (p) => (p.topCompetenceName ?? '').toLowerCase(),
        render: (p) =>
          p.topCompetenceName === null ? (
            <span className="coverage-cell__count">{PLACEHOLDER}</span>
          ) : (
            <span className="coverage-name__label">{p.topCompetenceName}</span>
          ),
      },
      {
        key: 'competenceProficiency',
        header: 'Proficiency',
        sortable: true,
        // Le persone senza dato (`null`) restano sempre in coda.
        sortComparator: (a, b, direction) => {
          const av = a.topProficiencyPercent;
          const bv = b.topProficiencyPercent;
          if (av === null && bv === null) return 0;
          if (av === null) return 1;
          if (bv === null) return -1;
          const sign = direction === 'desc' ? -1 : 1;
          return sign * (av - bv);
        },
        render: (p) =>
          p.topProficiencyPercent === null ? (
            <span className="coverage-cell__count">{PLACEHOLDER}</span>
          ) : (
            <div className="coverage-cell">
              <PercentBar value={p.topProficiencyPercent} label="Proficiency" />
              <span
                className={[
                  'coverage-cell__count',
                  p.topProficiencyPercent > 100 ? 'coverage-cell__count--gold' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {formatMatch(p.topProficiencyPercent)}
              </span>
            </div>
          ),
      },
      {
        key: 'topJob',
        header: 'Professione',
        sortable: true,
        sortAccessor: (p) => (p.topJobName ?? '').toLowerCase(),
        render: (p) =>
          p.topJobName === null ? (
            <span className="coverage-cell__count">{PLACEHOLDER}</span>
          ) : (
            <span className="coverage-name__label">{p.topJobName}</span>
          ),
      },
      {
        key: 'proficiency',
        header: 'Proficiency',
        sortable: true,
        // Le persone senza dato (`null`) restano sempre in coda.
        sortComparator: (a, b, direction) => {
          const av = a.topJobProficiencyPercent;
          const bv = b.topJobProficiencyPercent;
          if (av === null && bv === null) return 0;
          if (av === null) return 1;
          if (bv === null) return -1;
          const sign = direction === 'desc' ? -1 : 1;
          return sign * (av - bv);
        },
        render: (p) =>
          p.topJobProficiencyPercent === null ? (
            <span className="coverage-cell__count">{PLACEHOLDER}</span>
          ) : (
            <div className="coverage-cell">
              <PercentBar value={p.topJobProficiencyPercent} label="Proficiency" />
              <span
                className={[
                  'coverage-cell__count',
                  p.topJobProficiencyPercent > 100 ? 'coverage-cell__count--gold' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {formatMatch(p.topJobProficiencyPercent)}
              </span>
            </div>
          ),
      },
      {
        key: 'createdAt',
        header: 'Creato il',
        sortable: true,
        sortAccessor: (p) => p.createdAt,
        render: (p) => formatDate(p.createdAt),
      },
      {
        key: 'aiStatus',
        // Colonna senza header: lo stato della pre-compilazione AI è reso con
        // la sola icona (dentro il chip, che conserva i colori del tono).
        header: '',
        render: (p) => (
          <Badge
            tone={AI_STATUS_TONE[p.aiStatus]}
            className="external-cv-list__ai-status"
          >
            <Icon
              name={AI_STATUS_ICON[p.aiStatus]}
              size={16}
              title={AI_STATUS_LABEL[p.aiStatus]}
            />
          </Badge>
        ),
      },
    ],
    [],
  );

  return (
    <section className="external-cv-list" aria-labelledby="external-cv-list-title">
      <PageHeader
        title="Candidati"
        titleId="external-cv-list-title"
        subtitle="Carica il CV di un candidato esterno e ottieni le valutazioni delle skill pre-compilate dall'AI."
        actions={
          <>
            <Button
              type="button"
              variant="ghost"
              onClick={() =>
                window.open('/public/submission', '_blank', 'noopener,noreferrer')
              }
            >
              Link pubblico di candidatura
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => {
                resetForm();
                setIsCreateOpen(true);
              }}
            >
              Nuovo profilo
            </Button>
          </>
        }
      />

      {profilesQuery.isLoading ? (
        <Loading>Caricamento profili…</Loading>
      ) : profilesQuery.isError ? (
        <Alert tone="error">Impossibile caricare i candidati.</Alert>
      ) : (
        <>
          <Field
            id="external-cv-search"
            label="Cerca candidato"
            hint="Filtra per nome, cognome o professione principale."
          >
            <TextInput
              id="external-cv-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca per nome, cognome o professione…"
              autoComplete="off"
              aria-label="Cerca candidato per nome, cognome o professione principale"
            />
          </Field>
          <DataTable
            columns={columns}
            rows={visibleProfiles}
            rowKey={(p) => p.id}
            defaultSort={{ key: 'createdAt', direction: 'desc' }}
            emptyMessage={
              normalizedQuery === ''
                ? 'Nessun profilo CV esterno. Creane uno nuovo caricando un CV in PDF.'
                : 'Nessun candidato corrisponde alla ricerca.'
            }
          />
        </>
      )}

      {/* Creazione */}
      <Modal
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="Nuovo profilo CV esterno"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setIsCreateOpen(false)}>
              Annulla
            </Button>
            <Button
              type="submit"
              form="external-cv-create-form"
              variant="primary"
              loading={createProfile.isPending}
              disabled={!canSubmit}
            >
              Crea
            </Button>
          </>
        }
      >
        <form
          id="external-cv-create-form"
          className="admin-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!canSubmit || pdfFile === null) return;
            setFormError(null);
            createProfile.mutate({
              firstName: trimmedFirstName,
              lastName: trimmedLastName,
              file: pdfFile,
            });
          }}
        >
          <Field id="external-cv-first-name" label="Nome">
            <TextInput
              id="external-cv-first-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Es. Mario"
            />
          </Field>
          <Field id="external-cv-last-name" label="Cognome">
            <TextInput
              id="external-cv-last-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Es. Rossi"
            />
          </Field>
          <Field
            id="external-cv-pdf"
            label="CV (PDF)"
            hint="Carica il CV del candidato in formato PDF. Verrà analizzato dall'AI per pre-compilare le valutazioni."
          >
            <input
              ref={fileInputRef}
              id="external-cv-pdf"
              type="file"
              accept={PDF_CONTENT_TYPE}
              onChange={(e) => setPdfFile(e.target.files?.[0] ?? null)}
            />
          </Field>

          {createProfile.isPending && (
            <Alert tone="info">
              Caricamento del CV e pre-compilazione AI in corso…
            </Alert>
          )}
          {formError !== null && <Alert tone="error">{formError}</Alert>}
        </form>
      </Modal>
    </section>
  );
}
